import * as commentsWidgetsController from 'OK/CommentWidgetsController';
import * as dom from 'OK/utils/dom';
import {getInstance as getIcon} from 'OK/LazyIconsCache';
import {applyContent} from "OK/LazyIconNative";

interface IWidgetInfo {
    /**
     * Текущее кол-во.
     */
    count: number;
    /**
     * Элементы виджетов.
     */
    elements: HTMLElement[];
    /**
     * Максимальное число, которое может показывать виджет.
     */
    maxCount: number;
}

interface IWidgetSet {
    [id: string]: IWidgetInfo;
}

const widgetSet: IWidgetSet = {};

const ICON_COMMENT = 'ico_comment_widget_16';
const ICON_COMMENT_OFF = 'ico_comment_off_widget_16';

export default class CommentWidgetsNew {
    protected discussionId!: string;
    protected discussionType!: number;
    protected element!: HTMLElement;
    protected location!: string;

    static setCountInWidgets(key: string, count: number) {
        const widget = widgetSet[key];
        if (widget) {
            const {elements} = widget;
            if (Array.isArray(elements)) {
                elements.forEach(function (element) {
                    const countCnt = dom.firstByClass(element, 'js-count');

                    // Если "новый" виджет и число находится в отдельном спане.
                    if (countCnt) {
                        const parent = dom.parent(element, 'widget')!;

                        parent.classList.toggle('__null', count < 1);
                        countCnt.classList.toggle('__empty', count < 1);

                        const maxCount = widget.maxCount;
                        if (maxCount && count > maxCount) {
                            countCnt.textContent = maxCount + '+';
                        } else {
                            countCnt.textContent = String(count);
                        }
                    }
                });
            }
        }
    }

    static setEnabled(key: string, enabled: boolean) {
        const widget = widgetSet[key];

        if (!widget) {
            return;
        }

        const {elements} = widget;
        if (!Array.isArray(elements)) {
            return;
        }

        const icon = enabled ? ICON_COMMENT : ICON_COMMENT_OFF;
        const iconClass = `svg-${icon}`;

        const needRefresh = elements.some(el => {
            const icon = el.querySelector<SVGElement>('.widget_ico');
            return icon && !icon.classList.contains(iconClass);
        });

        if (!needRefresh) {
            return;
        }

        const iconCache = getIcon(icon);

        iconCache
            .load()
            .then(() => {
                elements.forEach(el => {
                    const icon = el.querySelector<SVGElement>('.widget_ico');
                    if (!icon || icon.classList.contains(iconClass)) {
                        // иконка корректная, ничего не делаем
                        return;
                    }
                    icon.classList.toggle(`svg-${ICON_COMMENT}`, enabled);
                    icon.classList.toggle(`svg-${ICON_COMMENT_OFF}`, !enabled);
                    applyContent(icon, iconCache);
                })
            });
    }

    add(id: string, type: number, element: HTMLElement) {
        const counterElement = dom.firstByClass(element, 'js-count');

        // Если "новый" виджет и число находится в отдельном спане.
        if (element && counterElement) {
            const key = commentsWidgetsController.makeKey(id, type);
            let widget = widgetSet[key];
            if (!widget) {
                const max = parseInt(counterElement.getAttribute('data-maxCount')!, 10);
                widget = widgetSet[key] = {
                    count: 0,
                    maxCount: isNaN(max) ? Infinity : max,
                    elements: []
                };
            }

            const text = counterElement.textContent!.replace(/\s/g, '');
            // Добавляем только если в счетчике число, а не надпись типа "99+"
            if (!isNaN(+text)) {
                const currentCount = parseInt(text, 10);
                if (!isNaN(currentCount)) {
                    widget.elements.push(element);
                    commentsWidgetsController.registerCounter(key, currentCount);
                }
            }
        }
    }

    remove(id: string, type: number, element: HTMLElement) {
        const key = commentsWidgetsController.makeKey(id, type);
        const widget = widgetSet[key];

        if (widget && element) {
            const domElements = widget.elements;
            const index = domElements ? domElements.indexOf(element) : -1;

            if (index > -1) {
                domElements.splice(index, 1);
                domElements.length === 0 && delete widgetSet[key];
                commentsWidgetsController.unregisterCounter(key);
            }
        }
    }

    handleEvent(e: MouseEvent) {
        const openDiscussionFn = commentsWidgetsController.getOpenDiscussionFn();
        e.preventDefault();
        if (openDiscussionFn) {
            openDiscussionFn(this.discussionId, this.discussionType, this.location);
        }
    }

    activate(element: HTMLElement) {
        this.element = element;
        this.discussionId = element.getAttribute('data-id')!;
        this.discussionType = parseInt(element.getAttribute('data-type')!, 10);
        this.location = element.getAttribute('data-location')!;
        this.add(this.discussionId, this.discussionType, element);
        if (element.getAttribute('data-bind') !== 'false') {
            element.addEventListener('click', this);
        }
    }

    deactivate() {
        this.element.removeEventListener('click', this);

        this.remove(this.discussionId, this.discussionType, this.element);
    }
}

commentsWidgetsController.addListener(CommentWidgetsNew);
